import { type StatusStyleType } from '@europrocurement/l2d-modules/hooks/useStatusStyle';
import { DossierStatus } from '@europrocurement/l2d-modules/modules/Dossiers';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, SxProps, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';

type DashboardStatCardTotalBarItems = {
    id?: number | null;
    libelle?: string | null;
    childsCount?: number | null;
    key: string;
};

type DashboardStatCardTotalBarProps = {
    items: DashboardStatCardTotalBarItems[];
    title: string;
    icon?: IconProp;
    handleItemClick: (statutId?: number | null | undefined) => void;
    handleStyleStatus?: (statutId?: number | null | undefined) => StatusStyleType;
};
const DashboardStatCardTotalBar: React.FunctionComponent<DashboardStatCardTotalBarProps> =
    function ({ items, title, icon, handleItemClick, handleStyleStatus }) {
        const { palette } = useTheme();

        const totalItems = useMemo(
            () => items.reduce((sum, item) => sum + (item.childsCount ?? 0), 0),
            [items],
        );

        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="l2dbodys">{title}</Typography>
                        <Typography variant="l2dh4">{totalItems}</Typography>
                    </Box>
                    {icon && (
                        <Box
                            sx={{
                                backgroundColor: palette.background.default,
                                padding: '6px',
                                fontSize: '24px',
                                borderRadius: '8px',
                            }}
                        >
                            <FontAwesomeIcon icon={icon} />
                        </Box>
                    )}
                </Box>
                <Box
                    sx={{ display: 'flex', width: '100%', borderRadius: '8px', overflow: 'hidden' }}
                >
                    {items.map((item) => {
                        const percentage =
                            totalItems > 0 ? ((item.childsCount ?? 0) / totalItems) * 100 : 0;

                        let sxStatus: SxProps = {};

                        if (handleStyleStatus && typeof handleStyleStatus !== 'undefined') {
                            sxStatus = handleStyleStatus(item.id as number).sxStatus;
                        }
                        return (
                            <Tooltip
                                title={item.libelle}
                                arrow
                                placement="top"
                                key={`status_bar_${item.key}`}
                            >
                                <Box
                                    sx={{
                                        height: '10px',
                                        width: `${percentage}%`,
                                        ...sxStatus,
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>

                <Box sx={{ display: 'flex', width: '100%', gap: '8px' }}>
                    {items.map((item) => {
                        if (item.childsCount && item.childsCount > 0) {
                            return (
                                <Tooltip
                                    title={item.libelle}
                                    arrow
                                    placement="top"
                                    key={`status_total_${item.key}`}
                                >
                                    <Box
                                        onClick={() => handleItemClick(item.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <DossierStatus
                                            label={item.childsCount.toString()}
                                            statutId={item.id as number}
                                            handleStyleStatus={handleStyleStatus}
                                        />
                                    </Box>
                                </Tooltip>
                            );
                        }
                        return null;
                    })}
                </Box>
            </>
        );
    };

export default DashboardStatCardTotalBar;
